import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Check,
  RotateCw,
} from "lucide-react";
import { fileUrl } from "../components/Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Name of the album used to store regular travel "Moments" photos.
// A separate album ("Shamanic") is managed from the Shamanic Path page.
const MOMENTS_ALBUM_NAME = "Moments";

// Renders an image with optional rotation (0/90/180/270) WITHOUT black bars.
// Measures natural dimensions on load so the wrapper aspect-ratio matches
// the rotated image exactly.
const RotatedImage = ({ src, alt, rotation, className = "", imgClassName = "", eager = false }) => {
  const [nat, setNat] = useState(null);
  const r = rotation || 0;
  const isSide = r === 90 || r === 270;

  const aspectRatio = nat
    ? isSide
      ? `${nat.h} / ${nat.w}`
      : `${nat.w} / ${nat.h}`
    : undefined;

  return (
    <div className={`relative w-full ${className}`} style={{ aspectRatio }}>
      <img
        src={src}
        alt={alt || ""}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onLoad={(e) =>
          setNat({
            w: e.currentTarget.naturalWidth,
            h: e.currentTarget.naturalHeight,
          })
        }
        style={
          isSide && nat
            ? {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) rotate(${r}deg)`,
                width: `calc(100% * ${nat.w} / ${nat.h})`,
                height: `calc(100% * ${nat.h} / ${nat.w})`,
                maxWidth: "none",
              }
            : r === 180
            ? { transform: "rotate(180deg)" }
            : undefined
        }
        className={
          isSide && nat ? imgClassName : `block w-full h-auto ${imgClassName}`
        }
      />
    </div>
  );
};

export default function MomentsPage() {
  const [photos, setPhotos] = useState([]);
  const [albumId, setAlbumId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);

  // Resolve the single internal album we use to store everything
  const resolveAlbum = async () => {
    try {
      const { data } = await axios.get(`${API}/albums`);
      if (Array.isArray(data)) {
        const target = data.find((a) => a.name === MOMENTS_ALBUM_NAME);
        if (target) return target.id;
      }
      const { data: created } = await axios.post(
        `${API}/albums`,
        { name: MOMENTS_ALBUM_NAME },
        { withCredentials: true }
      );
      return created.id;
    } catch {
      return null;
    }
  };

  const loadPhotos = async () => {
    try {
      const id = await resolveAlbum();
      if (id) setAlbumId(id);
      const url = id
        ? `${API}/photos?limit=500&album_id=${id}`
        : `${API}/photos?limit=500`;
      const { data } = await axios.get(url);
      setPhotos(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Couldn't load the gallery.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPhotos();
    resolveAlbum().then(setAlbumId);
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setIsAdmin(!!data.is_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let targetId = albumId || (await resolveAlbum());
    if (!targetId) {
      toast.error("No upload target available.");
      e.target.value = "";
      return;
    }
    if (!albumId) setAlbumId(targetId);
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/albums/${targetId}/photos`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} photo(s) added.`);
      loadPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`, { withCredentials: true });
      setPhotos((all) => all.filter((p) => p.id !== photoId));
    } catch {
      toast.error("Couldn't remove the photo.");
    }
  };

  const rotatePhoto = async (photo) => {
    const next = ((photo.rotation || 0) + 90) % 360;
    // Optimistic update
    setPhotos((all) => all.map((p) => (p.id === photo.id ? { ...p, rotation: next } : p)));
    try {
      await axios.patch(
        `${API}/photos/${photo.id}`,
        { rotation: next },
        { withCredentials: true }
      );
    } catch {
      toast.error("Couldn't rotate the photo.");
      // Revert on error
      setPhotos((all) =>
        all.map((p) => (p.id === photo.id ? { ...p, rotation: photo.rotation || 0 } : p))
      );
    }
  };

  const savePhoto = async () => {
    if (!editingPhoto) return;
    try {
      await axios.patch(
        `${API}/photos/${editingPhoto.id}`,
        { place: editingPhoto.place, year: editingPhoto.year },
        { withCredentials: true }
      );
      setPhotos((all) =>
        all.map((p) =>
          p.id === editingPhoto.id
            ? { ...p, place: editingPhoto.place, year: editingPhoto.year }
            : p
        )
      );
      toast.success("Saved.");
      setEditingPhoto(null);
    } catch {
      toast.error("Couldn't save.");
    }
  };

  return (
    <div data-testid="moments-page" className="relative z-10 min-h-screen">
      <header className="sticky top-0 z-50 bg-[#06040A]/80 backdrop-blur-xl border-b border-[#2C223A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="moments-logo">
            <img
              src="/ovidiu-logo.png"
              alt="Ovidiu"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-[#A59DAB] hover:text-[#D6B56D] transition-colors"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-6 text-outline-strong">
            The full journal
          </p>
          <h1 className="font-heading font-light text-5xl md:text-7xl text-[#EBE4D8] text-outline-strong">
            Moments
          </h1>
          <p className="text-[#EBE4D8] mt-5 max-w-lg mx-auto font-light text-outline-strong">
            Fragments from the road — places, people, and quiet in-between hours.
          </p>
        </motion.div>

        {/* Admin upload */}
        {isAdmin && (
          <div className="mb-10 flex justify-center">
            <label
              data-testid="upload-photos-label"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all cursor-pointer"
            >
              <Upload size={16} /> {uploading ? "Uploading..." : "Add photos"}
              <input
                data-testid="upload-photos-input"
                type="file"
                accept="image/*"
                multiple
                onChange={uploadPhotos}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>
        )}

        {loading ? (
          <p className="text-center text-[#A59DAB]">Loading...</p>
        ) : photos.length === 0 ? (
          <p data-testid="photos-empty" className="text-center text-[#A59DAB] font-light py-16">
            No photos yet{isAdmin ? " — use the button above to add some." : "."}
          </p>
        ) : (
          <div
            data-testid="masonry-gallery"
            className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]"
          >
            {photos.map((p, i) => (
              <motion.figure
                key={p.id}
                data-testid={`moment-photo-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 8) * 0.03 }}
                onClick={() => setLightboxIndex(i)}
                className="group relative mb-4 break-inside-avoid rounded-2xl overflow-hidden border border-[#2C223A] hover:border-[#D6B56D]/55 transition-colors cursor-pointer bg-[#0c0a14]"
              >
                <RotatedImage
                  src={fileUrl(p.image_path, 700)}
                  alt={p.place || ""}
                  rotation={p.rotation || 0}
                  eager={i < 8}
                  imgClassName="transition-transform duration-700 group-hover:scale-[1.03]"
                />

                {(p.place || p.year) && (
                  <figcaption className="pointer-events-none absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-[#06040A]/90 via-[#06040A]/35 to-transparent">
                    <p
                      data-testid={`moment-caption-${i}`}
                      className="photo-caption-shadow text-sm md:text-base font-medium text-[#EBE4D8] leading-tight"
                    >
                      {p.place}
                      {p.place && p.year ? " · " : ""}
                      {p.year}
                    </p>
                  </figcaption>
                )}

                {isAdmin && (
                  <>
                    <button
                      data-testid={`moment-delete-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Delete this photo?")) deletePhoto(p.id);
                      }}
                      aria-label="Delete photo"
                      className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#06040A]/75 text-[#EBE4D8] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500/85 transition-all"
                    >
                      <Trash2 size={13} />
                    </button>
                    <button
                      data-testid={`moment-edit-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingPhoto({
                          id: p.id,
                          place: p.place || "",
                          year: p.year || "",
                        });
                      }}
                      aria-label="Edit caption"
                      className="absolute top-2 left-2 w-9 h-9 rounded-full bg-[#06040A]/75 text-[#EBE4D8] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      data-testid={`moment-rotate-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        rotatePhoto(p);
                      }}
                      aria-label="Rotate photo"
                      className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-[#06040A]/75 text-[#EBE4D8] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                    >
                      <RotateCw size={13} />
                    </button>
                  </>
                )}

                {editingPhoto?.id === p.id && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="absolute inset-0 bg-[#06040A]/95 p-4 flex flex-col justify-center gap-3"
                  >
                    <input
                      data-testid={`photo-place-input-${p.id}`}
                      value={editingPhoto.place}
                      onChange={(e) =>
                        setEditingPhoto({ ...editingPhoto, place: e.target.value })
                      }
                      placeholder="Place (e.g. Athos, Greece)"
                      className="w-full bg-transparent border-b border-[#2C223A] focus:border-[#D6B56D] text-[#EBE4D8] placeholder:text-[#A59DAB]/60 text-sm py-1.5 outline-none"
                    />
                    <input
                      data-testid={`photo-year-input-${p.id}`}
                      value={editingPhoto.year}
                      onChange={(e) =>
                        setEditingPhoto({ ...editingPhoto, year: e.target.value })
                      }
                      placeholder="Year (optional)"
                      className="w-full bg-transparent border-b border-[#2C223A] focus:border-[#D6B56D] text-[#EBE4D8] placeholder:text-[#A59DAB]/60 text-sm py-1.5 outline-none"
                    />
                    <div className="flex gap-2 mt-1">
                      <button
                        data-testid={`photo-save-${p.id}`}
                        onClick={savePhoto}
                        className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-full bg-[#D6B56D] text-[#06040A] text-xs uppercase tracking-wide font-medium hover:bg-[#F0D494]"
                      >
                        <Check size={13} /> Save
                      </button>
                      <button
                        onClick={() => setEditingPhoto(null)}
                        className="px-3 py-1.5 rounded-full border border-[#2C223A] text-[#A59DAB] text-xs uppercase tracking-wide hover:border-[#D6B56D]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </motion.figure>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && photos[lightboxIndex] && (
          <motion.div
            data-testid="photo-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIndex(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#05070F]/95 backdrop-blur-md p-4"
          >
            <button
              data-testid="photo-lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(null);
              }}
              aria-label="Close"
              className="absolute top-5 right-5 z-[95] w-11 h-11 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
            >
              <X size={20} />
            </button>
            {photos.length > 1 && (
              <>
                <button
                  data-testid="photo-lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((x) => (x - 1 + photos.length) % photos.length);
                  }}
                  aria-label="Previous"
                  className="absolute left-4 md:left-8 z-[95] w-11 h-11 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  data-testid="photo-lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((x) => (x + 1) % photos.length);
                  }}
                  aria-label="Next"
                  className="absolute right-4 md:right-8 z-[95] w-11 h-11 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.div
              key={lightboxIndex}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative inline-block"
            >
              {(() => {
                const r = photos[lightboxIndex].rotation || 0;
                const isSide = r === 90 || r === 270;
                return (
                  <img
                    data-testid="photo-lightbox-image"
                    src={fileUrl(photos[lightboxIndex].image_path, 1600)}
                    alt=""
                    decoding="async"
                    style={{
                      transform: r ? `rotate(${r}deg)` : undefined,
                      maxHeight: isSide ? "92vw" : "88vh",
                      maxWidth: isSide ? "88vh" : "92vw",
                    }}
                    className="block object-contain rounded-xl"
                  />
                );
              })()}
              {(photos[lightboxIndex].place || photos[lightboxIndex].year) && (
                <div className="absolute bottom-0 inset-x-0 rounded-b-xl px-4 py-4 bg-gradient-to-t from-[#06040A]/90 via-[#06040A]/35 to-transparent">
                  <p
                    data-testid="photo-lightbox-caption"
                    className="photo-caption-shadow text-center text-base md:text-lg font-medium text-[#EBE4D8]"
                  >
                    {photos[lightboxIndex].place}
                    {photos[lightboxIndex].place && photos[lightboxIndex].year ? " · " : ""}
                    {photos[lightboxIndex].year}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
